<template>
  <div class="news-detail-page">
    <!-- 头部组件 -->
    <Headers></Headers>

    <!-- 面包屑 -->
    <div class="breadcrumb w">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ name: 'CompanyIndustryNews' }">新闻资讯</el-breadcrumb-item>
        <el-breadcrumb-item>文章详情</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 详情内容 -->
    <div class="detail-container w">
      <!-- 加载状态 -->
      <div
        class="loading-wrap"
        v-if="loading"
        v-loading="loading"
        element-loading-text="正在加载文章详情..."
        element-loading-spinner="el-icon-loading"
      ></div>

      <!-- 空数据/加载失败 -->
      <div class="empty-wrap" v-else-if="!currentNews">
        <img src="https://picsum.photos/200/150?random=100" alt="空数据" class="empty-img" />
        <p class="empty-text">{{ errorMsg || '暂无该文章的详情内容' }}</p>
      </div>

      <!-- 文章主体：仅保留标题、发布信息、文字+图片 -->
      <div class="news-detail" v-else>
        <h2 class="detail-title">{{ currentNews.title }}</h2>
        <div class="detail-meta">
          <span>{{ formatTime(currentNews.publishTime || currentNews.createTime) }}</span>
          <span>来源：{{ currentNews.source || '未知来源' }}</span>
        </div>

        <!-- 文章内容：仅文字+图片（新增外层容器实现整体居中） -->
        <div class="content-wrapper">
          <div class="detail-content" v-html="currentNews.content || '<p>暂无详细内容</p>'"></div>
        </div>

        <!-- 返回按钮 -->
        <div class="back-btn">
          <el-button type="default" @click="goBack">返回新闻列表</el-button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <el-dialog title="图片预览" :visible.sync="imagePreviewVisible" width="900px" center destroy-on-close>
      <img :src="previewImageUrl" alt="预览" class="preview-img" />
    </el-dialog>

    <!-- 底部组件 -->
    <Bottom></Bottom>
  </div>
</template>

<script>
import Headers from "../components/headers";
import Bottom from "../components/bottom";
import { Message } from "element-ui";
import axios from "axios";

export default {
  name: "NewsDetail",
  components: { Headers, Bottom },
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      currentNews: null,
      imagePreviewVisible: false,
      previewImageUrl: "",
      loading: false,
      errorMsg: "",
      // 👉 关键：填写文章详情接口URL（替换为实际接口地址）
      newsDetailApiUrl: "https://dy01.txcfgl.net:24081/tx-api/api/v1/article/article-detail"
    };
  },
  created() {
    this.getNewsDetail();
  },
  mounted() {
    this.bindContentImageClick();
  },
  watch: {
    currentNews() {
      this.$nextTick(() => this.bindContentImageClick());
    },
    id: {
      handler() {
        this.getNewsDetail();
      },
      immediate: false
    }
  },
  methods: {
    // 获取文章详情（对接真实接口）
    async getNewsDetail() {
      this.loading = true;
      this.errorMsg = "";
      this.currentNews = null;

      try {
        // 构造请求参数（根据后端要求调整）
        const requestData = {
          id: this.id // 文章ID
        };

        // 调用接口（与列表页保持一致的请求方式）
        const response = await axios.get(this.newsDetailApiUrl, {
          params: requestData, // GET参数在这里传递, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        });

        // 根据后端实际返回格式调整（假设返回格式：{ code: '00000', data: {}, message: 'success' }）
        if (response.data && response.data.code === '00000') {
          this.currentNews = response.data.data || {};
          // 数据格式兼容处理
          this.formatNewsData();
        } else {
          this.errorMsg = response.data.message || "获取文章详情失败";
          Message.error(this.errorMsg);
        }
      } catch (error) {
        console.error("文章详情请求失败:", error);
        this.errorMsg = "网络异常，无法加载文章详情";
        Message.error(this.errorMsg);
      } finally {
        this.loading = false;
      }
    },

    // 格式化新闻数据（统一数据格式，避免渲染错误）
    formatNewsData() {
      if (!this.currentNews) return;

      // 兼容不同字段名（根据后端实际返回调整）
      this.currentNews = {
        title: this.currentNews.title || "无标题",
        content: this.currentNews.content || "<p>暂无详细内容</p>",
        publishTime: this.currentNews.publishTime || this.currentNews.createTime || "",
        source: this.currentNews.source || "未知来源",
        // 其他可能需要的字段
        id: this.currentNews.id || this.id
      };

      // 处理内容中的图片（确保图片可预览）
      this.processContentImages();
    },

    // 处理内容中的图片（添加样式和预览属性）
    processContentImages() {
      if (!this.currentNews.content) return;

      // 给内容中的图片添加统一类名和data-original属性
      this.currentNews.content = this.currentNews.content.replace(
        /<img([^>]+)>/gi,
        (match, attr) => {
          // 提取src属性
          const srcMatch = attr.match(/src="([^"]+)"/);
          const src = srcMatch ? srcMatch[1] : "";

          // 添加统一类名、样式和data-original属性
          return `<img src="${src}" data-original="${src}" class="content-img" style="max-width:100%;height:auto;margin:20px auto;display:block;border-radius:8px;cursor:pointer;" alt="文章图片" />`;
        }
      );
    },

    // 绑定图片点击预览
    bindContentImageClick() {
      if (!this.currentNews) return;

      document.querySelectorAll('.content-img').forEach(img => {
        img.onclick = () => {
          this.previewImageUrl = img.dataset.original || img.src;
          this.imagePreviewVisible = true;
        };
      });
    },

    // 格式化时间（与列表页保持一致）
    formatTime(timeStr) {
      if (!timeStr) return "未知时间";
      try {
        const date = new Date(timeStr);
        if (isNaN(date.getTime())) return timeStr;
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      } catch (error) {
        return timeStr;
      }
    },

    // 返回列表
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style lang="less" scoped>
.news-detail-page {
  min-height: 100vh;
  padding-bottom: 50px;
}
.w {
  width: 1200px;
  margin: 0 auto;
}
.breadcrumb {
  padding: 20px 0;
  font-size: 14px;
  :deep(.el-breadcrumb__item:last-child .el-breadcrumb__link) {
    color: #c8171d;
    font-weight: bold;
  }
}
.detail-container {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 40px;
  min-height: 600px; // 确保加载状态和空状态高度一致
}

// 加载状态样式
.loading-wrap {
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-wrap {
  height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .empty-img {
    width: 200px;
    height: 150px;
    opacity: 0.5;
    margin-bottom: 20px;
  }
  .empty-text {
    font-size: 16px;
    color: #999;
  }
}
.news-detail {
  .detail-title {
    font-size: 32px;
    font-weight: bold;
    color: #333;
    text-align: center;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .detail-meta {
    text-align: center;
    font-size: 14px;
    color: #999;
    margin-bottom: 40px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
    span {
      margin: 0 15px;
    }
  }

  // 文章内容外层容器，实现整体居中
  .content-wrapper {
    width: 80%; // 控制文章内容的宽度，可根据需求调整
    margin: 0 auto; // 核心：左右margin自动实现居中
  }

  .detail-content {
    font-size: 18px;
    color: #333;
    line-height: 2;
    
    p {
      margin-bottom: 30px;
      text-indent: 2em; // 首行缩进
      text-align: left; // 段落文字左对齐
    }

    // 图片样式（与JS处理的样式保持一致）
    .content-img {
      max-width: 100%;
      height: auto;
      margin: 20px auto;
      display: block;
      border-radius: 8px;
      transition: all 0.3s;
      &:hover {
        transform: scale(1.02);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      }
    }

    // 兼容内容中的其他标签样式
    img {
      max-width: 100%;
      height: auto;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      th, td {
        border: 1px solid #eee;
        padding: 8px 12px;
        text-align: center;
      }
      th {
        background-color: #f8f8f8;
      }
    }
    ul, ol {
      margin: 15px 0;
      padding-left: 2em;
    }
    strong, b {
      color: #333;
    }
    em, i {
      color: #666;
    }
  }

  .back-btn {
    text-align: center;
    margin-top: 60px;
    :deep(.el-button) {
      padding: 10px 30px;
      border-color: #c8171d;
      color: #c8171d;
      font-size: 16px;
      &:hover {
        background-color: #c8171d;
        color: #fff;
      }
    }
  }
}
.preview-img {
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}

// 修复滚动条样式
::v-deep html {
  scroll-behavior: smooth;
}
</style>


// WEBPACK FOOTER //
// src/pages/reading.vue